(() => {
  if (customElements.get('hx-capture')) return;
  class HxCapture extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.launcher = this.querySelector('[data-capture-open]');
      this.form = this.querySelector('form');
      this.preview = this.dataset.preview === 'true';
      this.goal = 'energy';
      this.step = 'goal';
      this.key = this.preview ? 'hx-capture-preview-v1' : 'hx-capture-v1';
      if (!this.dialog || !this.form || typeof this.dialog.showModal !== 'function') {
        this.hidden = true;
        return;
      }
      if (this.preview && window.Shopify?.theme?.role === 'main' && !window.Shopify?.designMode) {
        this.hidden = true;
        return;
      }
      const options = { signal: this.abort.signal };
      this.launcher.addEventListener('click', () => this.open(), options);
      this.querySelectorAll('[data-capture-close]').forEach(button => button.addEventListener('click', () => this.close(), options));
      this.dialog.addEventListener('click', event => {
        const r = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)) this.close();
      }, options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.querySelector('[data-capture-next]').addEventListener('click', () => this.choose(), options);
      this.querySelector('[data-capture-skip]').addEventListener('click', () => this.close(), options);
      this.querySelector('[data-capture-back]').addEventListener('click', () => this.show('goal'), options);
      if (this.preview) this.querySelector('[data-capture-submit]').addEventListener('click', () => {
        if (this.form.reportValidity()) this.show('reward');
      }, options);
      this.form.addEventListener('submit', event => {
        if (this.preview) { event.preventDefault(); return; }
        if (this.dataset.provider === 'klaviyo') {
          event.preventDefault();
          this.subscribe();
          return;
        }
        this.querySelector('[data-capture-submit]').disabled = true;
        this.form.setAttribute('aria-busy', 'true');
        try { sessionStorage.setItem('hx-capture-pending-goal', this.goal); } catch (_) {}
      }, options);
      this.querySelector('[data-capture-copy]')?.addEventListener('click', () => this.copyCode(), options);
      if (this.querySelector('[data-capture-confirmed]')) {
        try { this.goal = sessionStorage.getItem('hx-capture-pending-goal') || 'energy'; sessionStorage.removeItem('hx-capture-pending-goal'); } catch (_) {}
        this.save({ subscribed: true });
        this.launcher.hidden = true;
        this.show('reward', false);
        this.open();
      } else if (this.querySelector('[data-capture-error]')) {
        this.show('email', false);
        this.open();
      } else {
        if (!this.preview && this.read().subscribed) this.launcher.hidden = true;
        this.timer = window.setTimeout(() => {
          const state = this.read();
          if (state.subscribed || (state.dismissedUntil || 0) > Date.now() || matchMedia('(max-width: 640px)').matches) return;
          if (this.preview || document.querySelector('hx-product')) return;
          this.waitForScroll = () => {
            const travel = document.documentElement.scrollHeight - innerHeight;
            if (travel > 0 && scrollY / travel >= .2 && !this.busy()) {
              window.removeEventListener('scroll', this.waitForScroll);
              this.open();
            }
          };
          window.addEventListener('scroll', this.waitForScroll, { passive: true, signal: this.abort.signal });
          this.waitForScroll();
        }, Number(this.dataset.delay || 35) * 1000);
      }
    }
    read() {
      try { return JSON.parse(localStorage.getItem(this.key) || '{}'); } catch (_) { return {}; }
    }
    async subscribe() {
      const consent = this.querySelector('[data-capture-consent]');
      if (this.submitting || !this.form.reportValidity() || !consent) return;
      const button = this.querySelector('[data-capture-submit]');
      const label = this.querySelector('[data-capture-submit-label]');
      const error = this.querySelector('[data-capture-submit-error]');
      const email = this.form.querySelector('input[type="email"]');
      const originalLabel = label.textContent;
      this.submitting = true;
      button.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      label.textContent = this.dataset.submitting;
      error.hidden = true;
      this.requestAbort = new AbortController();
      const timeout = setTimeout(() => this.requestAbort.abort(), 15000);
      try {
        if (!this.dataset.siteId || !this.dataset.listId) throw new Error('Missing subscription destination');
        const response = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(this.dataset.siteId), {
          method: 'POST',
          credentials: 'omit',
          signal: this.requestAbort.signal,
          headers: { 'Content-Type': 'application/vnd.api+json', 'Accept': 'application/vnd.api+json', 'revision': '2026-07-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Hexagon Capture Premium',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: email.value.trim(),
                      locale: this.dataset.locale,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                      properties: {
                        Country: { FR: 'France', BE: 'Belgium', ES: 'Spain' }[this.dataset.country] || this.dataset.countryName || '',
                        hx_capture_goal: this.goal,
                        hx_capture_locale: this.dataset.locale,
                        hx_capture_source: 'Hexagon Capture Premium',
                        hx_capture_coupon: this.querySelector('[data-capture-copy]')?.dataset.code || '',
                        hx_capture_consent_version: 'v2-submit',
                        hx_capture_consent_text: consent.textContent.trim(),
                        hx_capture_consent_action: originalLabel.trim()
                      }
                    }
                  }
                }
              },
              relationships: { list: { data: { type: 'list', id: this.dataset.listId } } }
            }
          })
        });
        if (response.status !== 202) throw new Error('Subscription not accepted');
        this.save({ subscribed: true });
        this.launcher.hidden = true;
        email.value = '';
        this.show('reward');
      } catch (_) {
        error.textContent = this.dataset.submitError;
        error.hidden = false;
      } finally {
        clearTimeout(timeout);
        this.submitting = false;
        button.disabled = false;
        label.textContent = originalLabel;
        this.form.removeAttribute('aria-busy');
      }
    }
    save(update) {
      try { localStorage.setItem(this.key, JSON.stringify({ ...this.read(), ...update })); } catch (_) {}
    }
    busy() {
      return document.documentElement.dataset.hxPurchaseIntent === 'true' || !!document.querySelector('dialog[open], details[open].menu-opening, cart-drawer.active, arte-cart-drawer[open], arte-cart-drawer.is-open, .v3-cart-drawer.is-open');
    }
    open() {
      if (this.dialog.open) return;
      this.dialog.showModal();
      this.querySelector(`[data-capture-panel="${this.step}"] h2`)?.focus({ preventScroll: true });
    }
    close() {
      clearTimeout(this.timer);
      if (this.waitForScroll) window.removeEventListener('scroll', this.waitForScroll);
      this.dialog.close();
      this.save({ dismissedUntil: Date.now() + 14 * 24 * 60 * 60 * 1000 });
      if (!this.launcher.hidden) this.launcher.focus({ preventScroll: true });
    }
    choose(skip = false) {
      this.goal = skip ? 'all' : this.querySelector('input[name="hx_capture_goal"]:checked')?.value || 'all';
      this.querySelector('[data-capture-tags]').value = this.dataset.tags + ',hx_goal_' + this.goal;
      this.show('email');
    }
    show(step, focus = true) {
      this.step = step;
      this.querySelectorAll('[data-capture-panel]').forEach(panel => panel.hidden = panel.dataset.capturePanel !== step);
      this.dialog.setAttribute('aria-labelledby', this.querySelector(`[data-capture-panel="${step}"] h2`).id || this.titleId(step));
      if (step === 'reward') {
        const items = [...this.querySelectorAll('[data-capture-recommendation]')];
        const selected = items.find(item => item.dataset.captureRecommendation === this.goal) || items[0];
        items.forEach(item => item.hidden = item !== selected);
      }
      if (focus && this.dialog.open) this.querySelector(`[data-capture-panel="${step}"] h2`)?.focus({ preventScroll: true });
      this.dialog.scrollTop = 0;
    }
    titleId(step) {
      const heading = this.querySelector(`[data-capture-panel="${step}"] h2`);
      heading.id = this.dialog.id + '-title-' + step;
      return heading.id;
    }
    async copyCode() {
      const button = this.querySelector('[data-capture-copy]');
      const status = this.querySelector('[data-capture-status]');
      document.dispatchEvent(new CustomEvent('hx:discount-request', { detail: { code: button.dataset.code } }));
      try {
        await navigator.clipboard.writeText(button.dataset.code);
        this.querySelector('[data-capture-copy-label]').textContent = this.dataset.copied;
        status.textContent = this.dataset.copied;
      } catch (_) { status.textContent = this.dataset.copyError; }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.requestAbort?.abort();
      if (this.dialog?.open) this.dialog.close();
    }
  }
  customElements.define('hx-capture', HxCapture);
})();
